// Utility function to create query strings from params
function createQueryString(params?: Record<string, unknown>): string {
  if (params === undefined || params === null) {
    return '';
  }
  return `?${new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  ).toString()}`;
}

/**
 * Static routes that don't depend on a space context
 */
export const frontEndRoutes = {
  home: '/',
  notAllowed: '/not-allowed',
  auth: {
    signIn: '/auth/signin',
    forgotPassword: '/auth/forgot-password',
  },
  dashboard: {
    /** Base path without space - used for redirect and path matching */
    base: '/dashboard',
  },
};

/**
 * Dashboard route type returned by getDashboardRoutes
 */
export type DashboardRoutes = ReturnType<typeof getDashboardRoutes>;

/**
 * Get dashboard routes parameterized by spaceId.
 * The spaceId is embedded in the URL path as the single source of truth.
 *
 * Usage:
 * ```ts
 * const routes = getDashboardRoutes(spaceId);
 * routes.course.base    // => '/dashboard/my-space/course'
 * routes.course.byId('123') // => '/dashboard/my-space/course/123'
 * ```
 */
export function getDashboardRoutes(spaceId: string) {
  const base = `/dashboard/${spaceId}`;

  return {
    base,

    course: {
      base: `${base}/course`,
      create: `${base}/course/new`,
      byId: (id: string, params?: Record<string, unknown>) =>
        `${base}/course/${id}${createQueryString(params)}`,
    },

    quiz: {
      base: `${base}/quiz`,
      create: `${base}/quiz/new`,
      byId: (id: string, params?: Record<string, unknown>) =>
        `${base}/quiz/${id}${createQueryString(params)}`,
    },

    sequence: {
      base: `${base}/sequence`,
      create: `${base}/sequence/new`,
      byId: (id: string, params?: Record<string, unknown>) =>
        `${base}/sequence/${id}${createQueryString(params)}`,
    },

    profile: {
      base: `${base}/profile`,
      byId: (id: string) => `${base}/profile/${id}`,
    },

    gallery: `${base}/gallery`,

    certificate: `${base}/certificate`,

    autocourse: {
      base: `${base}/course`,
      create: `${base}/course/?new=true`,
      list: (params?: Record<string, unknown>) =>
        `${base}/course/${createQueryString(params)}`,
      byId: (id: string) => `${base}/autocourse/${id}`,
    },

    abTest: {
      base: `${base}/abTest`,
      create: `${base}/abTest/?modal=true`,
      list: (params?: Record<string, unknown>) =>
        `${base}/abTest/${createQueryString(params)}`,
      byId: (id: string) => `${base}/abTest/?editingId=${id}`,
    },

    settings: `${base}/settings`,

    preferences: `${base}/preferences`,

    glossary: {
      base: `${base}/glossary`,
      create: `${base}/glossary/new`,
      byId: (id: string) => `${base}/glossary/${id}`,
    },

    jobProfile: {
      base: `${base}/jobProfile`,
      create: `${base}/jobProfile/new`,
      byId: (id: string) => `${base}/jobProfile/${id}`,
    },

    skillMatrix: {
      base: `${base}/skillMatrix`,
    },

    doc2course: `${base}/doc2course`,
  };
}

/**
 * Extract the spaceId from a dashboard pathname.
 * Returns empty string if the path doesn't match the dashboard/[spaceId] pattern.
 */
export function extractSpaceIdFromPathname(pathname: string): string {
  const match = pathname.match(/^\/dashboard\/([^/]+)/);
  if (match !== null && match[1] !== undefined) {
    return match[1];
  }
  return '';
}

/**
 * Replace the spaceId in a dashboard pathname with a new one.
 * Useful for space switching while staying on the same page.
 */
export function replaceSpaceInPathname(pathname: string, newSpaceId: string): string {
  return pathname.replace(/^\/dashboard\/[^/]+/, `/dashboard/${newSpaceId}`);
}
